// frontend/src/services/vehicleDbAPI.js
import api from './api';

/**
 * Vehicle Databases API Service
 * Handles all 7 premium APIs from Vehicle Databases
 */

const vehicleDbAPI = {
  // ========== MAINTENANCE API ==========
  maintenance: {
    /**
     * Get maintenance schedule by VIN
     * Backend: GET /vehicledatabases/maintenance/vin/{vin}
     */
    getByVIN: async (vin) => {
      try {
        const response = await api.get(`/vehicledatabases/maintenance/vin/${vin}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching maintenance by VIN:', error);
        throw error;
      }
    },

    /**
     * Get maintenance schedule by Year/Make/Model/Trim
     * Backend: GET /vehicledatabases/maintenance/ymmt/{year}/{make}/{model}/{trim}
     */
    getByYMMT: async (year, make, model, trim) => {
      try {
        // URL encode the trim as it may contain spaces
        const encodedTrim = encodeURIComponent(trim);
        const response = await api.get(
          `/vehicledatabases/maintenance/ymmt/${year}/${make}/${model}/${encodedTrim}`
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching maintenance by YMMT:', error);
        throw error;
      }
    },

    /**
     * Get maintenance schedule with caching and local completion merging
     * Backend: GET /api/vehicle/maintenance/{vehicle_profile_id}/schedule
     */
    getScheduleWithCache: async (vehicleProfileId, forceRefresh = false) => {
      try {
        const response = await api.get(`/vehicle/maintenance/${vehicleProfileId}/schedule`, {
          params: { force_refresh: forceRefresh }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching maintenance schedule with cache:', error);
        throw error;
      }
    },

    /**
     * Mark a maintenance service as completed
     * Backend: POST /api/vehicle/maintenance/{vehicle_profile_id}/complete
     */
    completeService: async (vehicleProfileId, serviceData) => {
      try {
        const response = await api.post(`/vehicle/maintenance/${vehicleProfileId}/complete`, {
          service_name: serviceData.service_name,
          mileage: serviceData.mileage,
          cost: serviceData.cost,
          provider: serviceData.provider,
          notes: serviceData.notes,
          invoice_number: serviceData.invoice_number,
          completion_date: serviceData.completion_date || new Date().toISOString().split('T')[0]
        });
        return response.data;
      } catch (error) {
        console.error('Error completing service:', error);
        throw error;
      }
    },

    /**
     * Transform raw API data to match UI format
     */
    transformMaintenanceData: (rawData, currentMileage = 45000) => {
      if (!rawData?.data) return null;

      const maintenanceData = rawData.data;
      const vehicleInfo = {
        vin: maintenanceData.vin,
        year: maintenanceData.year,
        make: maintenanceData.make,
        model: maintenanceData.model,
        trim: maintenanceData.trim,
        current_mileage: currentMileage
      };

      // Process maintenance schedule
      const allServices = [];
      const upcomingServices = [];
      const overdueServices = [];
      const maintenanceSchedule = [];
      const serviceCategories = {};
      const completedServices = [];

      // Process each mileage interval
      maintenanceData.maintenance?.forEach(interval => {
        const mileage = interval.mileage;
        
        // Handle different API response formats
        let serviceItems = [];
        if (interval.service_items) {
          // Direct service_items format (like Vehicle Databases API)
          serviceItems = interval.service_items;
        } else if (interval.severe?.service_items) {
          // Severe condition format
          serviceItems = interval.severe.service_items;
        } else if (interval.normal?.service_items) {
          // Normal condition format
          serviceItems = interval.normal.service_items;
        }
        
        if (!serviceItems || serviceItems.length === 0) return;

        serviceItems.forEach(service => {
          const serviceData = {
            service: service,
            due_at_miles: mileage,
            miles_until_due: mileage - currentMileage,
            estimated_cost: interval.costs ? {
              low: interval.costs.low,
              high: interval.costs.high,
              average: interval.costs.average
            } : { low: 0, high: 0, average: 0 },
            severity: 'medium', // Default, can be enhanced
            can_diy: service.toLowerCase().includes('inspect') || 
                    service.toLowerCase().includes('rotate'),
            time_estimate: 1.0 // Default estimate
          };

          // Categorize based on service type
          let category = 'general';
          if (service.toLowerCase().includes('oil')) category = 'engine';
          else if (service.toLowerCase().includes('brake')) category = 'brakes';
          else if (service.toLowerCase().includes('tire') || service.toLowerCase().includes('rotate')) category = 'tires';
          else if (service.toLowerCase().includes('transmission')) category = 'transmission';
          else if (service.toLowerCase().includes('filter')) category = 'filters';
          else if (service.toLowerCase().includes('coolant') || service.toLowerCase().includes('cooling')) category = 'cooling';

          if (!serviceCategories[category]) {
            serviceCategories[category] = [];
          }
          serviceCategories[category].push(serviceData);

          // Add to appropriate list based on mileage
          if (mileage > currentMileage) {
            // Upcoming service
            if (mileage - currentMileage <= 5000) {
              upcomingServices.push({
                ...serviceData,
                severity: mileage - currentMileage <= 1000 ? 'high' : 'medium'
              });
            }
            maintenanceSchedule.push(serviceData);
          } else if (mileage < currentMileage && !completedServices.find(s => s.service_name === service && s.completed_mileage === mileage)) {
            // Overdue service
            overdueServices.push({
              ...serviceData,
              overdue_by_miles: currentMileage - mileage,
              risk_level: Math.min(5, Math.ceil((currentMileage - mileage) / 5000))
            });
          }
        });
      });

      // Calculate cost analysis
      const maintenanceCostAnalysis = {
        next_year_estimate: calculateYearlyEstimate(maintenanceSchedule, currentMileage, 1),
        three_year_total: calculateYearlyEstimate(maintenanceSchedule, currentMileage, 3),
        three_year_average: calculateYearlyEstimate(maintenanceSchedule, currentMileage, 3) / 3,
        five_year_total: calculateYearlyEstimate(maintenanceSchedule, currentMileage, 5),
        five_year_average: calculateYearlyEstimate(maintenanceSchedule, currentMileage, 5) / 5,
        cost_per_mile: 0.075 // Default estimate
      };

      // Find next major service
      const nextMajorService = findNextMajorService(maintenanceSchedule, currentMileage);

      return {
        vehicle_info: vehicleInfo,
        data_source: 'vehicledatabases_api',
        upcoming_services: upcomingServices.slice(0, 10), // Limit to 10 most urgent
        overdue_services: overdueServices,
        maintenance_schedule: maintenanceSchedule,
        service_categories: serviceCategories,
        maintenance_cost_analysis: maintenanceCostAnalysis,
        next_major_service: nextMajorService,
        completed_services: completedServices,
        manufacturer_recommendations: [
          "Follow severe driving schedule if you frequently drive in stop-and-go traffic",
          "Check owner's manual for specific recommendations for your vehicle"
        ],
        severe_driving_adjustments: maintenanceData.maintenance?.some(m => m.conditions?.includes('severe') || m.severe)
      };
    }
  },

  // ========== REPAIRS API ==========
  repairs: {
    /**
     * Get common repairs by VIN
     */
    getByVIN: async (vin, mileageRange = null) => {
      const response = await api.get(`/vehicle/repairs/${vin}`, {
        params: { mileage_range: mileageRange }
      });
      return response.data;
    },

    /**
     * Get common repairs by Year/Make/Model
     */
    getByYMM: async (year, make, model, mileageRange = null) => {
      const response = await api.get('/vehicle/repairs/ymm', {
        params: { year, make, model, mileage_range: mileageRange }
      });
      return response.data;
    }
  },

  // ========== SPECIFICATIONS API ==========
  specifications: {
    /**
     * Get complete specifications by Year/Make/Model/Trim
     */
    getByYMM: async (year, make, model, trim = null) => {
      const response = await api.get('/vehicle/specifications/ymm', {
        params: { year, make, model, trim }
      });
      return response.data;
    }
  },

  // ========== PREMIUM VIN DECODE API ==========
  vinDecode: {
    /**
     * Premium Plus VIN Decode
     */
    decodePremium: async (vin, options = {}) => {
      const formData = new FormData();
      formData.append('vin', vin);
      formData.append('include_market_value', options.includeMarketValue || false);
      formData.append('include_specs', options.includeSpecs !== false);
      formData.append('include_equipment', options.includeEquipment !== false);

      const response = await api.post('/vehicle/vin/decode-premium', formData);
      return response.data;
    }
  },

  // ========== VIN OCR API ==========
  ocr: {
    /**
     * Scan VIN from image
     */
    scanVIN: async (imageFile, enhanceImage = true) => {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('enhance_image', enhanceImage);

      const response = await api.post('/vehicle/ocr/scan-vin', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },

    /**
     * Batch scan multiple images
     */
    batchScan: async (imageFiles) => {
      const formData = new FormData();
      imageFiles.forEach((file, index) => {
        formData.append('images', file);
      });

      const response = await api.post('/vehicle/ocr/batch-scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
  },

  // ========== MARKET VALUE API ==========
  marketValue: {
    /**
     * Get market value by VIN
     */
    getByVIN: async (vin, options = {}) => {
      const response = await api.get(`/vehicle/market-value/vin/${vin}`, {
        params: {
          zip_code: options.zipCode,
          mileage: options.mileage,
          condition: options.condition || 'good',
          include_historical: options.includeHistorical || false,
          include_forecast: options.includeForecast || false
        }
      });
      return response.data;
    },

    /**
     * Get market value by Year/Make/Model
     */
    getByYMM: async (year, make, model, options = {}) => {
      const response = await api.get('/vehicle/market-value/ymm', {
        params: {
          year,
          make,
          model,
          trim: options.trim,
          zip_code: options.zipCode,
          mileage: options.mileage,
          condition: options.condition || 'good'
        }
      });
      return response.data;
    }
  },

  // ========== ELECTRIC VEHICLE API ==========
  ev: {
    /**
     * Get EV specifications by VIN
     */
    getByVIN: async (vin, includeChargingNetwork = true) => {
      const response = await api.get(`/vehicle/ev/specifications/${vin}`, {
        params: { include_charging_network: includeChargingNetwork }
      });
      return response.data;
    },

    /**
     * Get EV specifications by Year/Make/Model
     */
    getByYMM: async (year, make, model) => {
      const response = await api.get('/vehicle/ev/specifications/ymm', {
        params: { year, make, model }
      });
      return response.data;
    }
  },

  // ========== COMBINED INTELLIGENCE ==========
  /**
   * Get all available data for a VIN in one call
   */
  getCompleteIntelligence: async (vin, currentMileage = null, zipCode = null) => {
    const response = await api.get(`/vehicle/${vin}/complete-intelligence`, {
      params: {
        current_mileage: currentMileage,
        zip_code: zipCode
      }
    });
    return response.data;
  },

  // ========== USAGE STATS ==========
  /**
   * Get API usage statistics
   */
  getUsageStats: async () => {
    const response = await api.get('/vehicle/usage/stats');
    return response.data;
  },

  // ========== HELPER METHODS ==========
  
  /**
   * Check if a feature requires payment
   */
  checkFeatureAccess: async (feature) => {
    try {
      const response = await api.get('/user/feature-access', {
        params: { feature: `vehicledb_${feature}` }
      });
      return response.data;
    } catch (error) {
      return { has_access: false, error: error.message };
    }
  },

  /**
   * Format condition for API
   */
  formatCondition: (condition) => {
    const conditions = {
      'Excellent': 'excellent',
      'Good': 'good',
      'Fair': 'fair',
      'Poor': 'poor'
    };
    return conditions[condition] || condition.toLowerCase();
  },

  /**
   * Format mileage range
   */
  formatMileageRange: (minMiles, maxMiles) => {
    return `${minMiles}-${maxMiles}`;
  },

  /**
   * Handle payment required responses
   */
  handlePaymentRequired: (response) => {
    if (response.data?.error === 'payment_required') {
      return {
        requiresPayment: true,
        price: response.data.price,
        message: response.data.message,
        action: response.data.action
      };
    }
    return response.data;
  },

  /**
   * Check if vehicle is electric from decode data
   */
  isElectricVehicle: (decodeData) => {
    return decodeData?.is_electric || 
           decodeData?.ev_data !== null ||
           ['BEV', 'PHEV', 'HEV'].includes(decodeData?.fuel_type);
  }
};

// Export specific error types
export class VehicleDBError extends Error {
  constructor(message, code, details) {
    super(message);
    this.name = 'VehicleDBError';
    this.code = code;
    this.details = details;
  }
}

export class PaymentRequiredError extends VehicleDBError {
  constructor(price, action) {
    super('Payment required', 'PAYMENT_REQUIRED', { price, action });
    this.name = 'PaymentRequiredError';
  }
}

export class FeatureNotAvailableError extends VehicleDBError {
  constructor(feature, availableInTiers) {
    super('Feature not available', 'FEATURE_NOT_AVAILABLE', { feature, availableInTiers });
    this.name = 'FeatureNotAvailableError';
  }
}

function calculateYearlyEstimate(schedule, currentMileage, years) {
  const avgMilesPerYear = 12000;
  const targetMileage = currentMileage + (avgMilesPerYear * years);
  
  let totalCost = 0;
  schedule.forEach(service => {
    if (service.due_at_miles > currentMileage && service.due_at_miles <= targetMileage) {
      totalCost += service.estimated_cost?.average || 0;
    }
  });
  
  return Math.round(totalCost);
}

function findNextMajorService(schedule, currentMileage) {
  const majorServices = ['spark plugs', 'transmission', 'coolant', 'brake'];
  
  for (let service of schedule) {
    if (service.due_at_miles > currentMileage) {
      const isMajor = majorServices.some(major => 
        service.service.toLowerCase().includes(major)
      );
      
      if (isMajor) {
        return {
          services: [service.service],
          mileage: service.due_at_miles,
          estimated_cost: service.estimated_cost,
          miles_away: service.due_at_miles - currentMileage
        };
      }
    }
  }
  
  return null;
}

export default vehicleDbAPI;